import { useId, useState } from "react";
import type { Currency } from "@nh/shared";
import { Navbar, NavBody } from "@/shared/ui/ui";
import { BrandLogo } from "./BrandLogo";
import { CartButton } from "./CartButton";
import { LocaleControls } from "./LocaleControls";
import { MenuButton } from "./MenuButton";
import { SessionGreeting } from "./SessionGreeting";
import { SignInButton } from "./SignInButton";
import { SignUpButton } from "./SignUpButton";
import { SiteMenuPanel, type SiteMenuItem } from "./SiteMenuPanel";

export interface SiteHeaderProps {
  /**
   * Moneda activa del sitio. La manda quien pinta la página, y desde el
   * 14-sep-2026 la decide el país de conexión: aquí solo se enseña, con
   * el selector de IDIOMA al lado (`LocaleControls`).
   */
  currency: Currency;
  /** Se conserva por compatibilidad; ya no hay mando que cambie la moneda. */
  onCurrencyChange?: (currency: Currency) => void;
  /** Se conserva por compatibilidad: la moneda está siempre decidida. */
  currencyLocked?: boolean;
  /** Artículos en el carrito. */
  cartCount?: number;
  onCartClick?: () => void;
  /** A dónde vuelve el logotipo. */
  homeTo?: string;
  /** Sustituye el mapa de navegación del panel del menú. */
  menuItems?: readonly SiteMenuItem[];
  /**
   * Quién tiene sesión, si alguien la tiene. Con él, la barra saluda
   * por su nombre y ofrece salir; sin él, ofrece entrar y registrarse.
   * Viene por props —no del contexto— por la misma regla que el
   * carrito: la barra se monta en una prueba sin levantar medio
   * sistema, y es la página quien sabe qué significa la sesión.
   */
  user?: { name: string; businessTo?: string } | null;
  onLogout?: () => void;
  className?: string;
}

/**
 * La barra superior del sitio: menú, acceso, logotipo, moneda y carrito.
 *
 * ── Por qué vive en `shared/ui` y no en la tienda ────────────────────
 * Es el CROMO del sitio, no una pieza del catálogo: el panel de
 * administración lleva la misma barra. Si viviera en `features/shop/`, el
 * panel tendría que importar de una feature ajena o copiarla — y una
 * copia significa que el día que cambie el logotipo cambie en un sitio y
 * en el otro no.
 *
 * ── Por qué no sabe nada de sesión ni de carrito ─────────────────────
 * Recibe `currency`, `cartCount` y sus callbacks por props. La barra no
 * consulta el contexto de sesión ni el del carrito a propósito: así se
 * monta en una prueba sin levantar medio sistema, y la página decide qué
 * significa "abrir el carrito" en su contexto.
 *
 * ── El logotipo queda centrado de verdad ─────────────────────────────
 * Los grupos laterales llevan `flex-1 basis-0`: reparten a partes iguales
 * el espacio sobrante, así que el centro cae exactamente en el medio sin
 * necesidad de posicionar el logotipo en absoluto. Con posicionamiento
 * absoluto —como en el sistema actual— el logotipo se encima con los
 * botones en cuanto la pantalla se estrecha.
 *
 * ── La barra respira (Anahí, 27-ago) ─────────────────────────────────
 * Arriba del todo es la barra de siempre, a lo ancho. Al hacer scroll se
 * encoge a una píldora de vidrio flotante y centrada (el "resizable
 * navbar" de la referencia, con la ropa del sistema). La animación vive
 * en `Navbar`/`NavBody` (`shared/ui/ui/resizable-navbar`); aquí solo va
 * el contenido, que es el mismo en los dos estados.
 */
export function SiteHeader({
  currency,
  cartCount = 0,
  onCartClick,
  homeTo = "/shop",
  menuItems,
  user,
  onLogout,
  className,
}: SiteHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();

  return (
    <>
      <Navbar className={className}>
        <NavBody className="items-center gap-2 px-4 py-3 sm:gap-3 sm:px-6 sm:py-3.5">
          {/* IZQUIERDA · menú + acceso */}
          <div className="flex min-w-0 flex-1 basis-0 items-center gap-2">
            <MenuButton
              onClick={() => setMenuOpen(true)}
              expanded={menuOpen}
              controls={menuId}
            />
            {/*
              Con sesión, la barra SALUDA y ofrece salir: entrar y que la
              tienda se vea idéntica es la mala experiencia que Anahí
              señaló (26-ago) — la señal de "sí entraste" tiene que estar
              a la vista.

              Sin sesión, acceso y registro. Los dos estados se esconden
              bajo `md`: en un teléfono no caben junto al logotipo y al
              carrito, y siguen estando dentro del panel del menú.
            */}
            {user ? (
              <SessionGreeting
                name={user.name}
                businessTo={user.businessTo}
                className="hidden md:flex"
              />
            ) : (
              <>
                <SignInButton className="hidden md:inline-flex" />
                <SignUpButton className="hidden md:inline-flex" />
              </>
            )}
          </div>

          {/* CENTRO · logotipo */}
          <BrandLogo to={homeTo} />

          {/* DERECHA · moneda (fija) + idioma + carrito */}
          <div className="flex min-w-0 flex-1 basis-0 items-center justify-end gap-2">
            <LocaleControls currency={currency} />
            <CartButton count={cartCount} onClick={onCartClick} />
          </div>
        </NavBody>
      </Navbar>

      <SiteMenuPanel
        id={menuId}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        items={menuItems}
        user={user}
        onLogout={onLogout}
      />
    </>
  );
}
